import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HeaderComp from '../Header/Header';
import utils from '../../Functions/utils';
import { getFormattedDate } from '../../Functions/getFormattedDate';

import Swal from 'sweetalert2';
import './charidy.css';

import piggyEmpty from '../../images/01.png';
import coinSvg from '../../images/02.png';

/**
 * CharidyComp – donate fixed sums.  
 * Piggy‑bank fills, can be emptied, and now shows a burst of 👍 emojis
 * (one per ₪) that float upward and fade.
 */
function CharidyComp() {
  const navigate = useNavigate();

  // ---------- State ----------
  const [userData, setUserData] = useState({});
  const [userDeposit, setUserDeposit] = useState([]);
  const [error, setError] = useState('');
  const [menuHeaderOpen, setMenuHeaderOpen] = useState(false);
  const [coinDrops, setCoinDrops] = useState([]); // [{id, count}]

  const coins = [1, 2, 5, 10, 20, 50];

  /** Sum inside the piggy‑bank (₪) */
  const piggyTotal = userDeposit.reduce((acc, cur) => acc + Number(cur.total), 0);

  // ---------- useEffect – load data ----------
  useEffect(() => {
    const fetchData = async () => {
      await fetchUserData();
      await fetchUserDeposit();
    };
    fetchData();
  }, []);

  const fetchUserData = async () => {
    try {
      const cached = localStorage.getItem('userData');
      if (cached) {
        setUserData(JSON.parse(cached));
      } else {
        const resp = await utils.requestData('GetUserData', {});
        setUserData(resp[0]);
        localStorage.setItem('userData', JSON.stringify(resp[0]));
      }
    } catch (err) {
      console.error('Error fetching userData:', err);
      setError(err.message);
    }
  };

  const fetchUserDeposit = async () => {
    try {
      const resp = await utils.requestData('GetUserDeposit', {});
      setUserDeposit(resp);
    } catch (err) {
      console.error('Error fetching userDeposit:', err);
      setError(err.message);
    }
  };

  // ---------- Coin‑drop animation helper ----------
  const triggerCoinDrop = (count) => {
    const id = Date.now();
    setCoinDrops((prev) => [...prev, { id, count }]);
    setTimeout(() => {
      setCoinDrops((prev) => prev.filter((c) => c.id !== id));
    }, 1000); // match CSS animation duration
  };

  // ---------- Donate ----------
  const donate = async (amount) => {
    const result = await Swal.fire({
      title: 'האם אתה בטוח?',
      text: `אתה עומד לתרום ${amount} ש"ח לצדקה`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'אישור',
      cancelButtonText: 'ביטול',
    });
    if (!result.isConfirmed) return;

    try {
      const resp = await utils.requestData('InsertDeposit', {
        total: amount,
        createAt: getFormattedDate(),
      });
      setUserDeposit(resp);
      await Swal.fire({
        title: 'תודה רבה!',
        html: `<div>התרומה נקלטה בהצלחה</div><div style="font-size: 13px; color: #555; margin-top: 8px;">החיוב יתבצע בסוף היום ויכלול את כל התרומות של אותו יום.</div>`,
        icon: 'success',
      });
      
      triggerCoinDrop(amount);
    } catch (err) {
      console.error('Error donate:', err);
      setError(err.message);
      Swal.fire('שגיאה', err.message, 'error');
    }
  };

  // ---------- Empty the piggy‑bank ----------
  const emptyPiggy = async () => {
    const result = await Swal.fire({
      title: 'לרוקן את הקופה?',
      text: 'פעולה זו תסיר את כל התרומות שנשמרו (ברמת ה‑UI).',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'אישור',
      cancelButtonText: 'ביטול',
    });
    if (!result.isConfirmed) return;
    setUserDeposit([]);
  };

  // ---------- Render ----------
  return (
    <div className="tanya-container">
      <HeaderComp userData={userData} menuHeaderOpen={menuHeaderOpen} setMenuHeaderOpen={setMenuHeaderOpen} />

      <h1 className="page-title">צדקה</h1>

      {/* Piggy‑bank visual */}
      <div className="piggy-wrapper">
        <div
          className="piggy-bank"
          style={{
            '--fill': `${Math.min(piggyTotal / 1000, 1) * 100}%`,
            '--piggy-img': `url(${piggyEmpty})`,
            // '--coin-img': `url(${coinSvg})`,
          }}
        ></div>
        {/* Coin drops */}
        {coinDrops.map((drop) =>
          [...Array(drop.count)].map((_, i) => (
            <img
              src={coinSvg}
              alt="coin"
              key={`${drop.id}-${i}`}
              className="coin-drop"
              style={{ left: `${48 + (Math.random() - 0.5) * 10}%`, animationDelay: `${i * 0.04}s` }}
            />
          ))
        )}
        <div className="piggy-amount">₪{piggyTotal}</div>
        {piggyTotal > 0 && (
          <button className="empty-btn" onClick={emptyPiggy}>
            רוקן קופה
          </button>
        )}
      </div>

      <h4>בחר סכום לצדקה</h4>

      <div className="item-grid">
        {coins.map((val) => (
          <div key={val} className="item-button" onClick={() => donate(val)}>
            {val}
          </div>
        ))}
      </div>

      <button className="button" onClick={() => navigate('/tanya')} style={{ marginTop: 20 }}>
        ⬅ חזור
      </button>
    </div>
  );
}

export default CharidyComp;

/*
==== charidy.css additions (append) =====
.like-icon {
  position: absolute;
  bottom: 20px;
  font-size: 24px;
  animation: floatLike 1.2s ease-out forwards;
  pointer-events: none;
}

@keyframes floatLike {
  0% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
  100% {
    transform: translateY(-120px) scale(1.4);
    opacity: 0;
  }
}
*/
